import { useState } from 'react';
import { SettingRow } from '@vnetwork/ui/layout';
import {
  Button,
  Badge,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
} from '@vnetwork/ui/primitives';
import { useToast, Alert } from '@vnetwork/ui/feedback';
import { Smartphone, Mail } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { tokenStorage } from '@/lib/token-storage';
import {
  useMfaStatus,
  useToggleEmailMfa,
  accountErrorMessage,
} from '@/api/account';
import { OtpSetupDialog } from './OtpSetupDialog';
import { DisableMfaDialog } from './DisableMfaDialog';

/**
 * Two-factor authentication management. Supports TOTP (authenticator app) for
 * all accounts, plus an email-code toggle for local accounts (SSO sessions get
 * MFA from their identity provider, so email MFA is hidden there).
 */
export function TwoFactorCard() {
  const { user } = useAuth();
  const { show } = useToast();
  const { data: status, isLoading, isError } = useMfaStatus();
  // Keycloak-backed sessions (SSO + Google) get MFA from the identity provider,
  // so the local email-MFA toggle is hidden for them.
  const isSso = tokenStorage.authMethod !== 'local';
  // Google (social) sign-in has NO password, so it could never pass the
  // password-confirmation required to DISABLE TOTP later. VNETWORK-ID ('sso') DOES
  // have a password (supports change-password + 2FA), so only Google is blocked.
  const isGoogle = tokenStorage.authMethod === 'google';

  const [otpOpen, setOtpOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);

  const enabled = Boolean(status?.enabled);
  const method = status?.method ?? null;

  const enableEmail = useToggleEmailMfa({
    onSuccess: () => show({ tone: 'success', title: 'Email two-factor enabled' }),
    onError: (e) =>
      show({
        tone: 'danger',
        title: 'Could not enable email two-factor',
        description: accountErrorMessage(e, 'Please try again.'),
      }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Two-factor authentication</CardTitle>
        <CardDescription>
          Add a second step at sign-in to keep your account secure.
        </CardDescription>
      </CardHeader>
      <CardBody>
        {isLoading ? (
        <Skeleton style={{ height: 96 }} />
      ) : isError ? (
        <Alert tone="warning" title="Two-factor status unavailable">
          We couldn't reach the security service. Refresh the page to try again.
        </Alert>
      ) : (
        <>
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {/* Authenticator app (TOTP) */}
          <SettingRow
            title={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                <Smartphone size={16} />
                Authenticator app
              </span>
            }
            description="Time-based codes from an app like Google Authenticator."
            control={
              enabled && method === 'totp' ? (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                  <Badge tone="success" appearance="subtle" size="sm">Enabled</Badge>
                  <Button variant="secondary" size="sm" onClick={() => setDisableOpen(true)}>
                    Disable
                  </Button>
                </div>
              ) : isGoogle ? (
                // Google sign-in has no password to later confirm a TOTP disable,
                // so don't let it enable TOTP. VNETWORK-ID + local accounts keep it.
                <Badge tone="neutral" appearance="subtle" size="sm">
                  Managed by Google sign-in
                </Badge>
              ) : (
                <Button size="sm" onClick={() => setOtpOpen(true)} disabled={enabled}>
                  Set up
                </Button>
              )
            }
          />

          {/* Email codes (local accounts only) */}
          {!isSso && (
            <SettingRow
              title={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                  <Mail size={16} />
                  Email codes
                </span>
              }
              description={`One-time codes sent to ${user?.email ?? 'your email'}.`}
              control={
                enabled && method === 'email' ? (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                    <Badge tone="success" appearance="subtle" size="sm">Enabled</Badge>
                    <Button variant="secondary" size="sm" onClick={() => setDisableOpen(true)}>
                      Disable
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={enabled || enableEmail.isPending}
                    onClick={() => enableEmail.mutate({ enable: true, email: user?.email })}
                  >
                    {enableEmail.isPending ? 'Enabling…' : 'Enable'}
                  </Button>
                )
              }
            />
          )}
          </div>

          {enabled && (
            <p style={{ margin: 'var(--vn-space-3) 0 0', fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
              Only one two-factor method can be active at a time. Disable the current method to switch.
            </p>
          )}
        </>
        )}
      </CardBody>

      <OtpSetupDialog open={otpOpen} onOpenChange={setOtpOpen} />
      <DisableMfaDialog open={disableOpen} method={method} onOpenChange={setDisableOpen} />
    </Card>
  );
}
